/**
 * CPA Topnotcher Protocol v5.0
 * 
 * Enterprise-grade cognitive framework for Philippine CPALE
 * Implements the "5-Step Cognitive Audit" for precision answers
 */

export interface CPALogicModule {
  name: string;
  triggers: string[];
  rules: string[];
  formulas?: string[];
  commonTraps?: string[];
}

/**
 * The 5-Step Cognitive Audit
 * AI must execute this before answering CPA questions
 */
export const COGNITIVE_AUDIT_STEPS = `
## 🧠 5-STEP COGNITIVE AUDIT (Execute Before Answering)

You are FORBIDDEN from answering immediately. Execute these steps:

1. **Trap Scan** - Check for distractors (List Price, Sunk Costs, Voidable vs. Void)
2. **Timeline Anchor** - Establish T₀ (Transaction Date) and T_End (Reporting Date)
3. **Authority Retrieval** - Cite specific Standard (e.g., PAS 16, Sec. 24 NIRC)
4. **Math Engine** - Set up equation using proper formulas. Do not estimate.
5. **"Why" Layer** - Explain the principle, not just the calculation
`;

/**
 * Subject-Specific Logic Modules
 */
export const CPA_LOGIC_MODULES: Record<string, CPALogicModule> = {
  FAR: {
    name: 'Financial Accounting (FAR) - The Historian',
    triggers: ['notes', 'bonds', 'loans', 'leases', 'inventory', 'fob', 'consignment', 'impairment'],
    rules: [
      'Time Value of Money: If stated rate ≠ market rate → Record at Present Value',
      'FOB Shipping Point: Buyer owns when it leaves seller dock',
      'FOB Destination: Seller owns until it hits buyer dock',
      'Consignment: Consignor owns, Consignee never owns',
      'Impairment: If CA > RA, Loss = CA - RA'
    ],
    formulas: [
      'Recoverable Amount = Higher of [FV - Cost of Disposal] OR [Value in Use]'
    ],
    commonTraps: [
      'Non-interest bearing → MUST impute interest',
      'Short-term (<1 year) → Ignore discounting (Materiality)'
    ]
  },

  AFAR: {
    name: 'Advanced Accounting (AFAR) - The Architect',
    triggers: ['partnership', 'liquidation', 'consolidation', 'goodwill', 'construction', 'percentage of completion'],
    rules: [
      'Partnership Liquidation: Use Loss Absorption Capacity (LAC) Method',
      'Quasi-Reorganization: Revalue to FV, eliminate RE deficit vs Share Premium',
      'Construction: Over Time (% Completion) vs Point in Time (Cost Recovery)'
    ],
    formulas: [
      'LAC = Partner Total Interest ÷ P&L Ratio',
      'Input Method: Actual Cost ÷ Total Est. Cost'
    ],
    commonTraps: [
      'Highest LAC gets paid first',
      'Pay creditors before partners'
    ]
  },

  MAS: {
    name: 'Management Advisory (MAS) - The Strategist',
    triggers: ['decision', 'relevant cost', 'variance', 'standard cost', 'budget', 'cvp'],
    rules: [
      'IGNORE: Sunk Costs, Allocated Fixed Costs, Depreciation (unless tax shield)',
      'FOCUS: Opportunity Cost (add to cost), Differential Cost (actual cash change)',
      'Variance: Unfavorable if Actual > Standard, Favorable if Actual < Standard'
    ],
    formulas: [
      'Material Price Variance: (AP - SP) × AQ',
      'Material Qty Variance: (AQ - SQ) × SP',
      'Labor Rate Variance: (AR - SR) × AH',
      'Labor Efficiency Variance: (AH - SH) × SR'
    ],
    commonTraps: [
      'Book value of old machine → IGNORE (sunk cost)',
      'Allocated overhead that won\'t change → IGNORE'
    ]
  },

  AUD: {
    name: 'Auditing (AUD) - The Skeptic',
    triggers: ['audit', 'risk', 'assertion', 'confirmation', 'test', 'evidence'],
    rules: [
      'Overstatement Test: Ledger → Source Documents (Existence)',
      'Understatement Test: Source Documents → Ledger (Completeness)',
      'Positive Confirmation: High risk, large balances',
      'Negative Confirmation: Low risk, small balances, many customers'
    ],
    commonTraps: [
      'Testing assets/income → Start from ledger',
      'Testing liabilities/expenses → Start from source docs'
    ]
  },

  TAX: {
    name: 'Taxation (TAX) - The Enforcer',
    triggers: ['tax', 'rcit', 'mcit', 'vat', 'withholding', 'situs', 'deduction'],
    rules: [
      'Situs - Service: Where performed',
      'Situs - Rent: Where property located',
      'Situs - Interest: Residence of debtor',
      'Situs - Dividend: Residence of corporation',
      'Bad Debts: Must be Worthless AND Written Off',
      'Charitable: Full to Gov/NGOs, Limited 5%(Corp) or 10%(Individual) of TI'
    ],
    commonTraps: [
      'Preponderance rule for foreign corporations',
      'Bad debt must be BOTH worthless AND written off'
    ]
  },

  RFBT: {
    name: 'Regulatory Framework (RFBT) - The Lawyer',
    triggers: ['contract', 'valid', 'void', 'voidable', 'corporation', 'pierce', 'liability'],
    rules: [
      'Valid: Binding',
      'Voidable: Valid until annulled (Defect: Consent)',
      'Rescissible: Valid but causes damage (Defect: Lesion)',
      'Void: No effect (Defect: Illegal object)',
      'Unenforceable: Cannot sue (Defect: Statute of Frauds)',
      'Pierce Corporate Veil: If used for fraud'
    ],
    commonTraps: [
      'Voidable ≠ Void (common exam trap)',
      'Oral sale of land → Unenforceable'
    ]
  }
};

/**
 * Common CPALE Trap Phrases
 */
export const CPALE_TRAPS: Record<string, { meaning: string; counterMove: string }> = {
  'List Price': {
    meaning: 'A starting price that is never recorded',
    counterMove: 'Deduct Trade Discounts immediately. Ignore Cash Discounts for initial recording (Gross Method)'
  },
  'Freight Collect, FOB Destination': {
    meaning: 'Buyer paid, but Seller should have',
    counterMove: 'Buyer has a Receivable from Seller. Do not expense it'
  },
  'Treasury Shares': {
    meaning: 'Company\'s own stock',
    counterMove: 'It is NOT an asset. It is a deduction from Equity'
  },
  'Salvage Value in DDB': {
    meaning: 'Residual value',
    counterMove: 'IGNORE it in the depreciation formula base, but STOP depreciating when Book Value hits this floor'
  },
  'Installment Liquidation': {
    meaning: 'Partnership cash payout',
    counterMove: 'Do NOT pay partners until all creditors are paid (or cash is reserved)'
  },
  'Non-interest bearing note': {
    meaning: 'Note with no stated interest rate',
    counterMove: 'MUST impute interest using market rate. Never record at face value'
  },
  'Book value of old equipment': {
    meaning: 'Historical cost of existing asset',
    counterMove: 'IGNORE in decision-making (sunk cost). Only consider disposal value'
  }
};

/**
 * Generate CPA-enhanced system instruction
 */
export function generateCPASystemInstruction(userQuestion: string): string {
  // Detect which module is relevant
  const relevantModules: string[] = [];
  const lowerQuestion = userQuestion.toLowerCase();

  Object.entries(CPA_LOGIC_MODULES).forEach(([key, module]) => {
    if (module.triggers.some(trigger => lowerQuestion.includes(trigger))) {
      relevantModules.push(key);
    }
  });

  // Detect trap phrases
  const detectedTraps: string[] = [];
  Object.keys(CPALE_TRAPS).forEach(trap => {
    if (lowerQuestion.includes(trap.toLowerCase())) {
      detectedTraps.push(trap);
    }
  });

  let instruction = COGNITIVE_AUDIT_STEPS;

  // Add relevant modules
  if (relevantModules.length > 0) {
    instruction += '\n\n## 📚 RELEVANT SUBJECT MODULES:\n';
    relevantModules.forEach(moduleKey => {
      const module = CPA_LOGIC_MODULES[moduleKey];
      instruction += `\n### ${module.name}\n`;
      instruction += `**Rules:**\n${module.rules.map(r => `- ${r}`).join('\n')}\n`;
      if (module.formulas) {
        instruction += `**Formulas:**\n${module.formulas.map(f => `- ${f}`).join('\n')}\n`;
      }
      if (module.commonTraps) {
        instruction += `**⚠️ Watch Out:**\n${module.commonTraps.map(t => `- ${t}`).join('\n')}\n`;
      }
    });
  }

  // Add trap warnings
  if (detectedTraps.length > 0) {
    instruction += '\n\n## 🚨 TRAP ALERT - Question Contains Common Distractors:\n';
    detectedTraps.forEach(trap => {
      const trapInfo = CPALE_TRAPS[trap];
      instruction += `\n**"${trap}"**\n`;
      instruction += `- Meaning: ${trapInfo.meaning}\n`;
      instruction += `- Counter-Move: ${trapInfo.counterMove}\n`;
    });
  }

  return instruction;
}

/**
 * Check if question requires CPA protocol
 */
export function requiresCPAProtocol(question: string): boolean {
  const cpaKeywords = [
    'pfrs', 'pas', 'nirc', 'depreciation', 'consolidation', 'partnership',
    'audit', 'tax', 'vat', 'rcit', 'goodwill', 'impairment', 'lease',
    'inventory', 'variance', 'standard cost', 'contract', 'void', 'voidable',
    'corporation', 'liquidation', 'construction', 'percentage of completion'
  ];

  const lowerQuestion = question.toLowerCase();
  return cpaKeywords.some(keyword => lowerQuestion.includes(keyword));
}
